// redux imports
import store from 'store'
import {
  accepted,
  connecting,
  mixMergedTracksWithDelay,
  sessionEnd,
  sessionTerminate,
  setWarmTransfer,
  parkSessionReject,
  mediaConnected,
  setPlayLocalRingTone,
} from 'redux/Sessions'
import { createLocalNotification } from 'redux/Notifications'
import { removeAllSipActions, setIsShown } from 'redux/Config'
// module imports
import eventLogger from 'modules/Logger'
import { callbackContact, clearCurrentContact } from 'modules/Contact'
import {
  forceNet2phoneCodecs,
  isMissedCall,
  isParkedCall,
} from 'modules/Sip/CustomSIP'
import { endAudioStream, startAudioStream } from 'modules/Audio/AudioStream'
import {
  showErrorSnackbar,
  showSuccessSnackbar,
} from 'modules/GlobalEvents/EventDispatchers'
// helper imports
import { isCSV3, isCSV2 } from 'modules/Helpers'
import { getActiveSession, parkCall } from 'modules/Sip/Helpers'
// constant imports
import NOTIFICATIONS from 'constants/Notifications'
import { MESSAGE_TYPE, ORIGIN, SETUP_TYPE } from 'constants/Sessions'

import { SPOG } from 'constants/Analytics'

const events = {
  accepted: (data, id) => {
    eventLogger.info('accepted', data)
    clearCurrentContact()
    store.dispatch(accepted(id))
    store.dispatch(removeAllSipActions())
  },
  connecting: (data, id) => {
    eventLogger.info('connecting', data)
    store.dispatch(connecting(id))

    // set session as warm transfer if necessary
    const { transferring, pendingId } = store.getState().sessions.warmTransfer
    const isWarmTransfer = transferring && pendingId === data.request.call_id

    if (isWarmTransfer) {
      store.dispatch(setWarmTransfer(id, true))
    }
  },
  ended: (data, id, getState) => {
    if (!store.getState().sessions.list[id]) return

    const { warmTransferring, parking } = store.getState().sessions.list[id]
    // NOTE no ID in response
    eventLogger.info('ended', data)

    // clear contact, call source, search
    clearCurrentContact()

    // end audio stream
    if (
      !warmTransferring &&
      !store.getState().sessions.warmTransfer.transferring
    ) {
      endAudioStream()
    }

    store.dispatch(removeAllSipActions())

    /**
     * Do not delete session related info if session was ended after parking before
     */
    if (parking && isCSV3()) return

    // check if call was parked
    const slot = isParkedCall(data?.message)

    if (slot) {
      parkCall(id, slot)
    }
    // remove session from redux
    store.dispatch(sessionEnd(data, id))

    if (
      !store.getState().sessions.list.length &&
      store.getState().config.consumer !== SPOG
    ) {
      store.dispatch(setIsShown(false))
    }

    const activeSession = getActiveSession(store.getState)

    if (activeSession) {
      startAudioStream(activeSession.connection, id, getState)
    }
  },
  failed: (data, id) => {
    // NOTE no ID in response
    eventLogger.error('failed', data)

    const { originator, cause, message } = data

    const showMissedCall = isMissedCall(message)

    const type = showMissedCall
      ? NOTIFICATIONS.sessionMap[originator][cause]
      : NOTIFICATIONS.names.callAnsweredElsewhere

    if (type) {
      const { participant: contact, incoming } =
        store.getState().sessions.list[id]
      // set callback contact for indirect calls
      const callBack = callbackContact(contact.callDetails)

      store.dispatch(
        createLocalNotification({ type, contact, callBack, incoming }),
      )
    }

    clearCurrentContact()

    store.dispatch(removeAllSipActions())

    // end session
    store.dispatch(sessionEnd(data, id))

    if (
      !store.getState().sessions.list.length &&
      store.getState().config.consumer !== SPOG
    ) {
      store.dispatch(setIsShown(false))
    }
  },
  sdp: (data) => {
    eventLogger.info('sdp', data)
    data.sdp = forceNet2phoneCodecs(data.sdp) // eslint-disable-line no-param-reassign
    // data mangling to enforce passive setup - fix for toggle hold with multiple sessions.
    if (
      store.getState().sessions.list.filter(Boolean).length > 1 &&
      data.originator === ORIGIN.REMOTE &&
      data.type === MESSAGE_TYPE.ANSWER
    ) {
      data.sdp = data.sdp.replace(SETUP_TYPE.ACTPASS, SETUP_TYPE.PASSIVE) // eslint-disable-line no-param-reassign

      // Looks like we need to mix tracks immediately if some of merged calls is outgoing
      // Otherwise wait for sdp answer signal and then mix it with small delay
      // Other part of this solution is in src/redux/Sessions/index.js -> 'mergeSessions' function
      store.dispatch(mixMergedTracksWithDelay())
    }
  },
  icecandidate: (candidate) => {
    // if callserver 2.0 do not wait for all icecandidates
    // because we need to reduce the connection time
    if (isCSV2()) {
      candidate.ready()
    }
  },
  peerconnection: (data, id, getState) => {
    eventLogger.info('peer_connection', data)
    // set audio track for incoming
    startAudioStream(data.peerconnection, id, getState)
  },
  progress: (data, id) => {
    const { status_code: statusCode = 0, headers = {} } = data?.response || {}
    if (statusCode === 183 || statusCode === 180) {
      if (headers['Content-Length']) {
        const [length = { parsed: 0 }] = headers['Content-Length'] || []
        if (length?.parsed > 0) {
          store.dispatch(mediaConnected(id))
        } else {
          // if we got 180/183 without early media we need to play local ting tones
          store.dispatch(setPlayLocalRingTone(id))
        }
      } else {
        // if we got 180/183 without early media we need to play local ting tones
        store.dispatch(setPlayLocalRingTone(id))
      }
    }
    data.body = null // eslint-disable-line no-param-reassign
  },
}

export const transferEvents = (notify = true) => ({
  accepted: (data) => {
    eventLogger.info('transfer_accepted', data)
    // send BYE for CSv3
    if (isCSV3()) {
      // get index of call to end
      /* eslint-disable camelcase */
      const { from_tag: fromTag, to_tag: toTag } = data.request
      const index = store.getState().sessions.list.findIndex((session) =>
        // eslint-disable-next-line no-underscore-dangle
        [fromTag, toTag].includes(session?.session?._from_tag),
      )
      /* eslint-enable camelcase */
      // validate index
      if (index < 0) return
      // terminate session if index found
      store.dispatch(sessionTerminate(index))
    }
  },
  requestFailed: (data) => {
    eventLogger.error('refer_request_failed', data)

    if (isCSV3()) {
      // get index of call to end
      /* eslint-disable camelcase */
      const { from_tag: fromTag, to_tag: toTag } = data.response
      const index = store.getState().sessions.list.findIndex((session) =>
        // eslint-disable-next-line no-underscore-dangle
        [fromTag, toTag].includes(session?.session?._from_tag),
      )

      /* eslint-enable camelcase */
      // validate index
      if (index < 0) return
      // terminate session if index found
      store.dispatch(parkSessionReject(index))
      store.dispatch(sessionTerminate(index))

      showErrorSnackbar('NOTIFICATIONS.PARK_FAILED', 'call-failed')
    }
  },
  requestSucceeded: (data) => {
    eventLogger.info('refer_request_succeeded', data)

    /**
     * To show success transfer snackbar also on 2.0 accounts
     * 'accepted' event isn't triggered for 2.0 accounts
     */
    if (notify) {
      showSuccessSnackbar(
        'NOTIFICATIONS.TRANSFERRED',
        'checkmark-circle-filled',
      )
    }
  },
})

export const setSessionListeners = (session, id, getState) => {
  Object.keys(events).forEach((event) => {
    session.on(event, (data) => {
      events[event](data, id, getState)
    })
  })
}
